/**
 * deliverableRegistry.ts — single source of truth for every deliverable type.
 *
 * Zero-dependency (no React, no imports) — safe to import from ui, features, and apps.
 *
 * Category model (2 categories):
 *   main  — radio button, exactly 1 required for approval, contributes to reach
 *   addon — checkbox, 0-N allowed, contributes to budget only (not reach)
 *
 * Usage rights is an addon with hasDays:true — NOT a third category.
 * Adding a new platform = add a Platform literal, add it to PLATFORMS, add registry entries.
 */

/** All supported campaign platforms. Extend here when adding a new platform. */
export type Platform = 'youtube' | 'instagram';

/** Ordered tuple of every known platform. Drives z.enum, IsValid, and resolvePlatform. */
export const PLATFORMS = ['youtube', 'instagram'] as const satisfies readonly Platform[];

/** Spellings the registry cannot derive: hostnames and legacy brand names. */
const PLATFORM_ALIASES: Readonly<Record<Platform, readonly string[]>> = {
  youtube: ['youtube', 'youtu'],
  instagram: ['instagram'],
};

/** Word tokens, so a substring never matches an alias by accident. */
function tokenize(raw: string): Set<string> {
  return new Set(raw.split(/[^a-z0-9]+/).filter(Boolean));
}

/** Display name per platform. */
export const PLATFORM_LABEL: Record<Platform, string> = {
  youtube: 'YouTube',
  instagram: 'Instagram',
};

/** Brand colour per platform, for the small platform dot/chip. */
export const PLATFORM_COLOR: Record<Platform, string> = {
  youtube: '#FF0000',
  instagram: 'linear-gradient(135deg,#833AB4,#FD1D1D,#FCB045)',
};

/** `PLATFORM_LABEL` with a fallback for an unknown/absent platform. */
export function platformLabel(raw: string | null | undefined, fallback = '—'): string {
  const p = resolvePlatform(raw);
  return p ? PLATFORM_LABEL[p] : fallback;
}

/** Null for unrecognised input: a plausible-but-wrong guess renders the wrong brand icon. */
export function resolvePlatform(raw: string | null | undefined): Platform | null {
  const p = (raw ?? '').trim().toLowerCase();
  if (!p) return null;

  const exact = PLATFORMS.find((platform) => platform === p);
  if (exact) return exact;

  const meta = DELIVERABLE_REGISTRY[p];
  if (meta && meta.platform !== '*') return meta.platform;

  const tokens = tokenize(p);
  return (
    PLATFORMS.find((platform) =>
      PLATFORM_ALIASES[platform].some((alias) => tokens.has(alias)),
    ) ?? null
  );
}

export type DeliverableCategory = 'main' | 'addon';

export interface DeliverableTypeMeta {
  platform: 'youtube' | 'instagram' | '*';
  category: DeliverableCategory;
  label: string;
  /** Only true for usage_rights — drives the days input in CommercialPopover and EditCreatorDrawer. */
  hasDays?: boolean;
}

export const DELIVERABLE_REGISTRY: Record<string, DeliverableTypeMeta> = {
  // ── YouTube ──────────────────────────────────────────────────────────────────
  youtube_integrated:        { platform: 'youtube',    category: 'main',  label: 'Integrated' },
  youtube_dedicated:         { platform: 'youtube',    category: 'main',  label: 'Dedicated' },
  youtube_shorts:            { platform: 'youtube',    category: 'main',  label: 'Shorts' },
  // ── Instagram main ────────────────────────────────────────────────────────────
  instagram_stories:         { platform: 'instagram',  category: 'main',  label: 'Stories' },
  instagram_static_post:     { platform: 'instagram',  category: 'main',  label: 'Static Post' },
  instagram_carousel:        { platform: 'instagram',  category: 'main',  label: 'Carousel' },
  instagram_non_collab_reel: { platform: 'instagram',  category: 'main',  label: 'Non Collab Reel' },
  instagram_collab_reel:     { platform: 'instagram',  category: 'main',  label: 'Collab Reel' },
  // ── Instagram add-ons ─────────────────────────────────────────────────────────
  instagram_link_in_bio:     { platform: 'instagram',  category: 'addon', label: 'Link in Bio' },
  instagram_link_in_dm:      { platform: 'instagram',  category: 'addon', label: 'Auto DM' },
  // ── Universal add-on ──────────────────────────────────────────────────────────
  usage_rights:              { platform: '*',           category: 'addon', label: 'Usage Rights', hasDays: true },
};

/** Returns true when `type` is a main-category deliverable on `platform`. */
export const isMain = (type: string, platform: string): boolean => {
  const meta = DELIVERABLE_REGISTRY[type];
  return !!meta && meta.category === 'main' && (meta.platform === '*' || meta.platform === platform);
};

/** Returns true when `type` is an add-on on `platform`. */
export const isAddon = (type: string, platform: string): boolean => {
  const meta = DELIVERABLE_REGISTRY[type];
  return !!meta && meta.category === 'addon' && (meta.platform === '*' || meta.platform === platform);
};

/** The main deliverable types that produce an Instagram Reel — mirrors the
 *  BE's ContentKindFromType (roster/domain/entry.go), which maps these two
 *  types to ContentKindReel and every other Instagram main to ContentKindPost.
 *  Official Audio Compliance is Reels-only: gates whether the "Campaign
 *  audio" step is offered at create time and, on the report, whether a
 *  live row can trigger an on-demand re-check. */
const REEL_DELIVERABLE_TYPES = new Set(['instagram_non_collab_reel', 'instagram_collab_reel']);

/** Returns true when `type` is a Reel deliverable (collab or non-collab). */
export const isReelDeliverable = (type: string): boolean => REEL_DELIVERABLE_TYPES.has(type);

/** Returns true when any of `types` is a Reel deliverable. */
export const hasReelDeliverable = (types: readonly string[]): boolean => types.some(isReelDeliverable);

/**
 * Campaign-policy predicate mirroring the BE's OfferedOnPlatform
 * (roster/domain/deliverable_registry.go): can a campaign on `platform` ever
 * offer this deliverable type? Platform-specific types are offered only on
 * their own platform. Universal ('*') types — usage_rights today — are offered
 * on Instagram only: Instagram exposes a usage-rights toggle at campaign
 * creation, YouTube does not yet. When YouTube gains usage rights, widen the
 * '*' case here and in the BE predicate together — every gated surface
 * (create form, BE create validation, sheet import) opens with no other change.
 *
 * Deliberately distinct from isMain/isAddon, which answer the *category*
 * question and treat '*' as valid on any platform.
 */
export const isOfferedOnPlatform = (type: string, platform: string): boolean => {
  const meta = DELIVERABLE_REGISTRY[type];
  if (!meta) return false;
  return meta.platform === '*' ? platform === 'instagram' : meta.platform === platform;
};

/**
 * Human-readable label for a deliverable type string.
 * Falls back to title-casing the stripped type name for unknown values.
 */
export const fmtDeliverableType = (type: string): string =>
  DELIVERABLE_REGISTRY[type]?.label ??
  type
    .replace(/^(youtube|instagram|tiktok|linkedin|facebook)_/i, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());

/** Which section of the import/edit UI a field belongs to. Declared here (not
 *  in roster.ts) because roster.ts imports it and this file has no imports. */
export type ImportFieldGroup = 'creator' | 'audience' | 'main' | 'addon';

export interface CreatorEditField {
  key: string;
  label: string;
  group: ImportFieldGroup;
  /** usage_rights only: the drawer renders the days input beside the price. */
  hasDays?: boolean;
}

const FOLLOWERS_LABEL: Record<Platform, string> = { instagram: 'Followers', youtube: 'Subscribers' };

const DRAWER_DELIVERABLE_ORDER: Record<Platform, readonly string[]> = {
  instagram: [
    'instagram_stories', 'instagram_static_post', 'instagram_carousel', 'instagram_non_collab_reel', 'instagram_collab_reel',
    'instagram_link_in_bio', 'instagram_link_in_dm', 'usage_rights',
  ],
  youtube: ['youtube_integrated', 'youtube_dedicated', 'youtube_shorts'],
};

export const CREATOR_EDIT_GROUP_LABEL: Record<ImportFieldGroup, string> = {
  creator: 'Creator',
  audience: 'Audience metrics',
  main: 'Main deliverables',
  addon: 'Add-ons',
};

const YOUTUBE_MAIN_GROUP_LABEL = 'Deliverable prices';

function audienceFields(platform: Platform): CreatorEditField[] {
  return [
    { key: 'followers', label: FOLLOWERS_LABEL[platform], group: 'audience' },
    { key: 'avg_views', label: 'Avg views', group: 'audience' },
    { key: 'engagement_rate', label: 'Engagement', group: 'audience' },
  ];
}

function deliverableEditField(type: string): CreatorEditField {
  const meta = DELIVERABLE_REGISTRY[type];
  return { key: type, label: meta?.label ?? fmtDeliverableType(type), group: meta?.category === 'main' ? 'main' : 'addon', hasDays: meta?.hasDays };
}

/** The Creators tab's "Edit creator data" field list for a platform — shared
 *  by EditCreatorDrawer and the sheet import so the two can't drift. */
export function creatorEditFields(platform: Platform): CreatorEditField[] {
  return [...audienceFields(platform), ...DRAWER_DELIVERABLE_ORDER[platform].map(deliverableEditField)];
}

export function creatorEditGroupLabel(group: ImportFieldGroup, platform: Platform): string {
  if (group === 'main' && platform === 'youtube') return YOUTUBE_MAIN_GROUP_LABEL;
  return CREATOR_EDIT_GROUP_LABEL[group];
}
